/**
 * Comprehensive Theme Switcher Component
 *
 * Floating action button that opens a panel for switching all 4 semantic layers:
 * - Layer 1: LayoutTheme (HOW to display)
 * - Layer 2: DesignTokenSet (VISUAL styling)
 * - Layer 3: ViewerProfile (WHAT to show)
 * - Layer 4: RolePersona (WHO evaluates)
 */

import { useState, useMemo } from 'react'
import { useLayoutTheme } from '@/contexts/LayoutThemeContext'
import { useDesignToken } from '@/contexts/DesignTokenContext'
import { useViewerProfile } from '@/contexts/ViewerProfileContext'
import { useRolePersona } from '@/contexts/RolePersonaContext'
import { useSession } from '@/lib/auth-client'
import type { LayoutTheme } from '@/types/layout-theme'
import type { DesignTokenSet } from '@/types/design-token-set'
import type { ViewerProfile } from '@/types/viewer-profile'
import type { RolePersona } from '@/types/role-persona'

export function ThemeSwitcher() {
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'layout' | 'tokens' | 'view' | 'role'>('layout')

  const { currentTheme, setTheme: setLayoutTheme, availableThemes } = useLayoutTheme()
  const { currentTokenSet, setTokenSet, availableTokenSets } = useDesignToken()
  const { currentProfile, setProfile, availableProfiles } = useViewerProfile()
  const { currentPersona, setPersona, availablePersonas } = useRolePersona()
  const { data: session } = useSession()

  // Check if user is superadmin (musia@habitusnet.ch)
  const isSuperAdmin = session?.user?.email === 'musia@habitusnet.ch'

  // Filter out terminal-explorer theme for non-superadmins
  const visibleThemes = useMemo(() => {
    return availableThemes.filter(theme => {
      if (theme === 'terminal-explorer' && !isSuperAdmin) {
        return false
      }
      return true
    })
  }, [availableThemes, isSuperAdmin])

  const togglePanel = () => setIsOpen(!isOpen)

  // Color swatches for design token sets (matching actual token definitions)
  const tokenColors: Record<DesignTokenSet, { primary: string; accent: string }> = {
    elegant: { primary: '#1a1a1a', accent: '#d4af37' },         // Charcoal + Gold
    'japanese-zen': { primary: '#2d3436', accent: '#6c7a89' },  // Sumi ink + Bamboo green-gray
    'scandinavian-hygge': { primary: '#34495e', accent: '#e67e22' },  // Slate blue + Terracotta orange
    'mediterranean-sun': { primary: '#0984e3', accent: '#d63031' },   // Azure blue + Terracotta red
  }

  // Labels for each option
  const layoutLabels: Record<LayoutTheme, string> = {
    'modern-cards': 'Modern Cards',
    'matrix-terminal': 'Matrix Terminal',
    'mac-os-classic': 'Mac OS Classic',
    'mac-studio': 'Mac Studio',
    'executive-dashboard': 'Executive Dashboard',
    'terminal-explorer': 'Terminal Explorer',
  }

  const tokenLabels: Record<DesignTokenSet, string> = {
    elegant: 'Elegant',
    'japanese-zen': 'Japanese Zen',
    'scandinavian-hygge': 'Scandinavian Hygge',
    'mediterranean-sun': 'Mediterranean Sun',
  }

  const viewLabels: Record<ViewerProfile, string> = {
    'public-view': 'Public View',
    'recruiter-view': 'Recruiter View',
    'technical-view': 'Technical View',
    'executive-view': 'Executive View',
  }

  const roleLabels: Record<RolePersona, string> = {
    candidate: 'Candidate',
    'hiring-manager': 'Hiring Manager',
    evaluator: 'Technical Evaluator',
    peer: 'Peer Reviewer',
  }

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={togglePanel}
        style={{
          position: 'fixed',
          bottom: 'var(--spacing-md)',
          right: 'var(--spacing-md)',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          backgroundColor: 'var(--color-accent)',
          color: '#fff',
          border: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          cursor: 'pointer',
          fontSize: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)'
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.4)'
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'scale(1)'
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)'
        }}
        aria-label="Open theme switcher"
      >
        🎨
      </button>

      {/* Switcher Panel */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: '88px',
            right: 'var(--spacing-md)',
            left: 'var(--spacing-md)',
            maxWidth: '360px',
            marginLeft: 'auto',
            maxHeight: '70vh',
            backgroundColor: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            zIndex: 999,
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.1)',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: 'var(--spacing-md)',
              borderBottom: '1px solid rgba(0,0,0,0.1)',
              backgroundColor: 'var(--color-background)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                }}
              >
                Theme Switcher
              </h3>
              <button
                onClick={togglePanel}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.25rem',
                  cursor: 'pointer',
                  color: 'var(--color-text-secondary)',
                  padding: '4px 8px',
                }}
              >
                ✕
              </button>
            </div>

            {/* Tabs */}
            <div
              style={{
                display: 'flex',
                gap: 'var(--spacing-xs)',
                marginTop: 'var(--spacing-md)',
              }}
            >
              {(['layout', 'tokens', 'view', 'role'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    flex: 1,
                    padding: 'var(--spacing-xs) var(--spacing-sm)',
                    backgroundColor:
                      activeTab === tab ? 'var(--color-primary)' : 'transparent',
                    color:
                      activeTab === tab
                        ? '#fff'
                        : 'var(--color-text-secondary)',
                    border: '1px solid',
                    borderColor:
                      activeTab === tab
                        ? 'var(--color-primary)'
                        : 'rgba(0,0,0,0.1)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                    transition: 'all 0.2s',
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div
            style={{
              padding: 'var(--spacing-md)',
              maxHeight: 'calc(70vh - 120px)',
              overflowY: 'auto',
            }}
          >
            {/* Layer 1: LayoutTheme */}
            {activeTab === 'layout' && (
              <div>
                <p
                  style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--spacing-md)',
                  }}
                >
                  Layer 1: Controls HOW content is displayed
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                  {visibleThemes.map((theme) => (
                    <button
                      key={theme}
                      onClick={() => setLayoutTheme(theme)}
                      style={{
                        padding: 'var(--spacing-md)',
                        backgroundColor:
                          currentTheme === theme
                            ? 'rgba(0,0,0,0.05)'
                            : 'transparent',
                        border: '1px solid',
                        borderColor:
                          currentTheme === theme
                            ? 'var(--color-accent)'
                            : 'rgba(0,0,0,0.1)',
                        borderRadius: 'var(--radius-md)',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          color: 'var(--color-text-primary)',
                          marginBottom: '4px',
                        }}
                      >
                        {currentTheme === theme && '✓ '}
                        {layoutLabels[theme]}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Layer 2: DesignTokenSet */}
            {activeTab === 'tokens' && (
              <div>
                <p
                  style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--spacing-md)',
                  }}
                >
                  Layer 2: Controls VISUAL styling
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                  {availableTokenSets.map((tokenSet) => (
                    <button
                      key={tokenSet}
                      onClick={() => setTokenSet(tokenSet)}
                      style={{
                        padding: 'var(--spacing-md)',
                        backgroundColor:
                          currentTokenSet === tokenSet
                            ? 'rgba(0,0,0,0.05)'
                            : 'transparent',
                        border: '1px solid',
                        borderColor:
                          currentTokenSet === tokenSet
                            ? 'var(--color-accent)'
                            : 'rgba(0,0,0,0.1)',
                        borderRadius: 'var(--radius-md)',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                        {/* Color Swatch */}
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <div
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '4px',
                              backgroundColor: tokenColors[tokenSet].primary,
                              border: '1px solid rgba(0,0,0,0.1)',
                            }}
                          />
                          <div
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '4px',
                              backgroundColor: tokenColors[tokenSet].accent,
                              border: '1px solid rgba(0,0,0,0.1)',
                            }}
                          />
                        </div>
                        {/* Label */}
                        <div>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              color: 'var(--color-text-primary)',
                            }}
                          >
                            {currentTokenSet === tokenSet && '✓ '}
                            {tokenLabels[tokenSet]}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Layer 3: ViewerProfile */}
            {activeTab === 'view' && (
              <div>
                <p
                  style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--spacing-md)',
                  }}
                >
                  Layer 3: Controls WHAT content is shown
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                  {availableProfiles.map((profile) => (
                    <button
                      key={profile}
                      onClick={() => setProfile(profile)}
                      style={{
                        padding: 'var(--spacing-md)',
                        backgroundColor:
                          currentProfile === profile
                            ? 'rgba(0,0,0,0.05)'
                            : 'transparent',
                        border: '1px solid',
                        borderColor:
                          currentProfile === profile
                            ? 'var(--color-accent)'
                            : 'rgba(0,0,0,0.1)',
                        borderRadius: 'var(--radius-md)',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          color: 'var(--color-text-primary)',
                          marginBottom: '4px',
                        }}
                      >
                        {currentProfile === profile && '✓ '}
                        {viewLabels[profile]}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Layer 4: RolePersona */}
            {activeTab === 'role' && (
              <div>
                <p
                  style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--spacing-md)',
                  }}
                >
                  Layer 4: Controls WHO evaluates (Power Reversal)
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                  {availablePersonas.map((persona) => (
                    <button
                      key={persona}
                      onClick={() => setPersona(persona)}
                      style={{
                        padding: 'var(--spacing-md)',
                        backgroundColor:
                          currentPersona === persona
                            ? 'rgba(0,0,0,0.05)'
                            : 'transparent',
                        border: '1px solid',
                        borderColor:
                          currentPersona === persona
                            ? 'var(--color-accent)'
                            : 'rgba(0,0,0,0.1)',
                        borderRadius: 'var(--radius-md)',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          color: 'var(--color-text-primary)',
                          marginBottom: '4px',
                        }}
                      >
                        {currentPersona === persona && '✓ '}
                        {roleLabels[persona]}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Overlay */}
      {isOpen && (
        <div
          onClick={togglePanel}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 998,
          }}
        />
      )}
    </>
  )
}
